// Helper functions to provide full localization for Blogs and BlogPost pages
// Supporting English (en), Marathi (mr), and Kannada (kn)

export const normalizeLang = (lang) => {
  if (!lang) return 'en';
  if (lang.startsWith('mr')) return 'mr';
  if (lang.startsWith('kn')) return 'kn';
  return 'en';
};

// ─── ON-DEMAND AUTO TRANSLATION HELPER ──────────────────────────────────────
// Translates text from English to target language (mr / kn) with caching and sentence/paragraph chunking
export const translateBlogText = async (text, targetLang) => {
  if (!text || !targetLang || targetLang === 'en') return text;
  const lang = normalizeLang(targetLang);
  if (lang === 'en') return text;

  const trimmed = text.trim();
  if (!trimmed) return '';

  const cacheKey = `blog_trans_${lang}_${encodeURIComponent(trimmed.slice(0, 100))}_${trimmed.length}`;
  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) return cached;
  } catch (e) {}

  // If short text (e.g. title, category, author, excerpt), translate in a single request
  if (trimmed.length <= 400) {
    try {
      const url = `https://api.mymemory.translated.net/get?q=${encodeURIComponent(trimmed)}&langpair=en|${lang}`;
      const res = await fetch(url);
      const data = await res.json();
      if (data && data.responseData && data.responseData.translatedText) {
        const translated = data.responseData.translatedText;
        try { localStorage.setItem(cacheKey, translated); } catch (e) {}
        return translated;
      }
    } catch (err) {
      console.warn('Auto translation failed:', err);
    }
    return text;
  }

  // If longer text (e.g. full blog article content), split by paragraphs first, then sentences
  const paragraphs = trimmed.split(/\n\s*\n/);
  const translatedParagraphs = [];

  for (const para of paragraphs) {
    if (!para.trim()) {
      translatedParagraphs.push('');
      continue;
    }

    if (para.length <= 400) {
      try {
        const url = `https://api.mymemory.translated.net/get?q=${encodeURIComponent(para)}&langpair=en|${lang}`;
        const res = await fetch(url);
        const data = await res.json();
        if (data && data.responseData && data.responseData.translatedText) {
          translatedParagraphs.push(data.responseData.translatedText);
          continue;
        }
      } catch (e) {}
    }

    // Split paragraph by sentences
    const sentences = para.split(/(?<=[.?!।\n])\s+/);
    const translatedSentences = [];
    for (const sentence of sentences) {
      if (!sentence.trim()) continue;
      try {
        const url = `https://api.mymemory.translated.net/get?q=${encodeURIComponent(sentence)}&langpair=en|${lang}`;
        const res = await fetch(url);
        const data = await res.json();
        if (data && data.responseData && data.responseData.translatedText) {
          translatedSentences.push(data.responseData.translatedText);
        } else {
          translatedSentences.push(sentence);
        }
      } catch (e) {
        translatedSentences.push(sentence);
      }
    }
    translatedParagraphs.push(translatedSentences.join(' '));
  }

  const result = translatedParagraphs.join('\n\n');
  try { localStorage.setItem(cacheKey, result); } catch (e) {}
  return result;
};

// ─── LOCALIZED CATEGORIES ───────────────────────────────────────────────────
export const getLocalizedBlogCategory = (category, t, lang = 'en', blog = null) => {
  if (!category) return '';
  const currentLang = normalizeLang(lang);

  // 1. Direct language field on blog object
  if (blog && typeof blog === 'object') {
    if (currentLang === 'mr' && (blog.category_mr || blog.categoryMr)) {
      return blog.category_mr || blog.categoryMr;
    }
    if (currentLang === 'kn' && (blog.category_kn || blog.categoryKn)) {
      return blog.category_kn || blog.categoryKn;
    }
  }

  const catLower = category.trim().toLowerCase();

  if (catLower === 'all') {
    return t ? t('catAll') : (currentLang === 'mr' ? 'सर्व' : currentLang === 'kn' ? 'ಎಲ್ಲಾ' : 'All');
  }

  if (catLower.includes('food processing') || catLower.includes('food')) {
    return currentLang === 'mr' ? 'अन्न प्रक्रिया तंत्रज्ञान' : currentLang === 'kn' ? 'ಆಹಾರ ಸಂಸ್ಕರಣಾ ತಂತ್ರಜ್ಞಾನ' : 'Food Processing Technology';
  }

  if (catLower.includes('industrial automation') || catLower.includes('automation')) {
    return currentLang === 'mr' ? 'औद्योगिक ऑटोमेशन' : currentLang === 'kn' ? 'ಕೈಗಾರಿಕಾ ಆಟೋಮೇಷನ್' : 'Industrial Automation';
  }

  if (catLower.includes('industry insights') || catLower.includes('insight')) {
    return currentLang === 'mr' ? 'उद्योग माहिती व ट्रेंड्स' : currentLang === 'kn' ? 'ಉದ್ಯಮದ ಒಳನೋಟಗಳು' : 'Industry Insights';
  }

  if (catLower.includes('bedana') || catLower.includes('raisin')) {
    return currentLang === 'mr' ? 'बेदाणा प्रक्रिया' : currentLang === 'kn' ? 'ಬೆದಾನ ಸಂಸ್ಕರಣೆ' : 'Bedana Processing';
  }

  if (catLower.includes('agriculture') || catLower.includes('agri')) {
    return currentLang === 'mr' ? 'कृषी तंत्रज्ञान' : currentLang === 'kn' ? 'ಕೃಷಿ ತಂತ್ರಜ್ಞಾನ' : 'Agricultural Technology';
  }

  // Check client-side cached translation
  try {
    const cacheKey = `blog_trans_${currentLang}_${encodeURIComponent(category.trim().slice(0, 100))}_${category.trim().length}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) return cached;
  } catch (e) {}

  return t ? t(category, category) : category;
};

// ─── LOCALIZED TITLES ───────────────────────────────────────────────────────
export const getLocalizedBlogTitle = (blog, lang = 'en') => {
  if (!blog) return '';
  const currentLang = normalizeLang(lang);
  const title = typeof blog === 'string' ? blog : (blog.title || '');
  if (currentLang === 'en') return title;

  // 1. Direct language field on blog object (dynamically saved via Admin)
  if (typeof blog === 'object') {
    if (currentLang === 'mr' && (blog.title_mr || blog.titleMr)) {
      return blog.title_mr || blog.titleMr;
    }
    if (currentLang === 'kn' && (blog.title_kn || blog.titleKn)) {
      return blog.title_kn || blog.titleKn;
    }
  }

  const lower = title.toLowerCase();

  // 2. Pre-defined dictionary for legacy blogs
  // Blog 1: Choosing the Right Food Processing Machinery...
  if (lower.includes('choosing the right') || (lower.includes('food processing') && lower.includes('growth'))) {
    if (currentLang === 'mr') return 'व्यवसायाच्या जास्तीत जास्त वाढीसाठी योग्य अन्न प्रक्रिया मशिनरी कशी निवडावी';
    if (currentLang === 'kn') return 'ಗರಿಷ್ಠ ವ್ಯಾಪಾರ ಬೆಳವಣಿಗೆಗಾಗಿ ಸರಿಯಾದ ಆಹಾರ ಸಂಸ್ಕರಣಾ ಯಂತ್ರೋಪಕರಣಗಳನ್ನು ಆಯ್ಕೆ ಮಾಡುವುದು ಹೇಗೆ';
    return title;
  }

  // Blog 2: How Conveyor Systems Improve Industrial Productivity...
  if (lower.includes('conveyor') && (lower.includes('productivity') || lower.includes('material handling'))) {
    if (currentLang === 'mr') return 'कन्व्हेयर सिस्टीम्स औद्योगिक उत्पादकता आणि मटेरिअल हँडलिंग कसे सुधारतात';
    if (currentLang === 'kn') return 'ಕನ್ವೇಯರ್ ಸಿಸ್ಟಮ್‌ಗಳು ಕೈಗಾರಿಕಾ ಉತ್ಪಾದಕತೆ ಮತ್ತು ಮೆಟೀರಿಯಲ್ ಹ್ಯಾಂಡ್ಲಿಂಗ್ ಅನ್ನು ಹೇಗೆ ಸುಧಾರಿಸುತ್ತವೆ';
    return title;
  }

  // Blog 3: Why Modern Bedana Processing Machines Are Essential...
  if (lower.includes('bedana') || (lower.includes('raisin') && lower.includes('essential'))) {
    if (currentLang === 'mr') return 'बेदाणा प्रक्रिया उद्योगांसाठी आधुनिक बेदाणा प्रक्रिया मशिन्स का आवश्यक आहेत';
    if (currentLang === 'kn') return 'ಒಣದ್ರಾಕ್ಷಿ ಸಂಸ್ಕರಣಾ ಉದ್ಯಮಗಳಿಗೆ ಆಧುನಿಕ ಬೆದಾನ ಸಂಸ್ಕರಣಾ ಯಂತ್ರಗಳು ಏಕೆ ಅತ್ಯಗತ್ಯ';
    return title;
  }

  // 3. Check client-side cached translation
  try {
    const cacheKey = `blog_trans_${currentLang}_${encodeURIComponent(title.trim().slice(0, 100))}_${title.trim().length}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) return cached;
  } catch (e) {}

  return title;
};

// ─── LOCALIZED EXCERPTS ─────────────────────────────────────────────────────
export const getLocalizedBlogExcerpt = (blog, lang = 'en') => {
  if (!blog) return '';
  const currentLang = normalizeLang(lang);
  const excerpt = typeof blog === 'string' ? blog : (blog.excerpt || '');
  if (currentLang === 'en') return excerpt;

  // 1. Direct language field on blog object
  if (typeof blog === 'object') {
    if (currentLang === 'mr' && (blog.excerpt_mr || blog.excerptMr)) {
      return blog.excerpt_mr || blog.excerptMr;
    }
    if (currentLang === 'kn' && (blog.excerpt_kn || blog.excerptKn)) {
      return blog.excerpt_kn || blog.excerptKn;
    }
  }

  const title = (blog.title || '').toLowerCase();

  // 2. Pre-defined dictionary for legacy blogs
  if (title.includes('choosing the right') || (title.includes('food processing') && title.includes('growth'))) {
    if (currentLang === 'mr') {
      return 'योग्य अन्न प्रक्रिया मशिनरी निवडल्याने उत्पादन कार्यक्षमता, उत्पादनाची गुणवत्ता आणि व्यवसायाचा दीर्घकालीन नफा वाढू शकतो. प्रत्येक उद्योजकाने काय जाणून घेतले पाहिजे ते येथे वाचा.';
    }
    if (currentLang === 'kn') {
      return 'ಸರಿಯಾದ ಆಹಾರ ಸಂಸ್ಕರಣಾ ಯಂತ್ರೋಪಕರಣಗಳನ್ನು ಆಯ್ಕೆ ಮಾಡುವುದರಿಂದ ಉತ್ಪಾದನಾ ದಕ್ಷತೆ, ಉತ್ಪನ್ನದ ಗುಣಮಟ್ಟ ಮತ್ತು ದೀರ್ಘಾವಧಿಯ ಲಾಭದಾಯಕತೆ ಹೆಚ್ಚುತ್ತದೆ. ಪ್ರತಿಯೊಬ್ಬ ಉದ್ಯಮಿಯೂ ತಿಳಿಯಬೇಕಾದ ಮಾಹಿತಿ ಇಲ್ಲಿದೆ.';
    }
    return excerpt;
  }

  if (title.includes('conveyor') && (title.includes('productivity') || title.includes('material handling'))) {
    if (currentLang === 'mr') {
      return 'आधुनिक कन्व्हेयर प्रणाली कशा प्रकारे मटेरिअल हँडलिंग सुव्यवस्थित करतात, मानवी श्रम कमी करतात आणि अन्न प्रक्रिया, कृषी व औद्योगिक कामकाजात उत्पादकता वाढवतात ते जाणून घ्या.';
    }
    if (currentLang === 'kn') {
      return 'ಆಧುನಿಕ ಕನ್ವೇಯರ್ ಸಿಸ್ಟಮ್‌ಗಳು ಮೆಟೀರಿಯಲ್ ಹ್ಯಾಂಡ್ಲಿಂಗ್ ಅನ್ನು ಹೇಗೆ ಸುಗಮಗೊಳಿಸುತ್ತವೆ, ಶ್ರಮವನ್ನು ಕಡಿಮೆ ಮಾಡುತ್ತವೆ ಮತ್ತು ಆಹಾರ ಸಂಸ್ಕರಣೆ ಹಾಗೂ ಕೈಗಾರಿಕೆಗಳಲ್ಲಿ ಉತ್ಪಾದಕತೆಯನ್ನು ಹೇಗೆ ಹೆಚ್ಚಿಸುತ್ತವೆ ಎಂಬುದನ್ನು ತಿಳಿಯಿರಿ.';
    }
    return excerpt;
  }

  if (title.includes('bedana') || (title.includes('raisin') && title.includes('essential'))) {
    if (currentLang === 'mr') {
      return 'आधुनिक बेदाणा प्रक्रिया मशिन्स कशा प्रकारे उत्पादकता वाढवतात, मानवी श्रम कमी करतात आणि महाराष्ट्र व भारतातील बेदाणा प्रक्रिया उद्योगांसाठी सातत्यपूर्ण गुणवत्ता देतात ते शोधा.';
    }
    if (currentLang === 'kn') {
      return 'ಆಧುನಿಕ ಬೆದಾನ ಸಂಸ್ಕರಣಾ ಯಂತ್ರಗಳು ಉತ್ಪಾದಕತೆಯನ್ನು ಹೇಗೆ ಸುಧಾರಿಸುತ್ತವೆ, ಶ್ರಮವನ್ನು ಹೇಗೆ ಕಡಿಮೆ ಮಾಡುತ್ತವೆ ಮತ್ತು ಮಹಾರಾಷ್ಟ್ರ ಹಾಗೂ ಭಾರತದಾದ್ಯಂತ ಸ್ಥಿರ ಗುಣಮಟ್ಟವನ್ನು ಹೇಗೆ ನೀಡುತ್ತವೆ ಎಂಬುದನ್ನು ತಿಳಿಯಿರಿ.';
    }
    return excerpt;
  }

  // 3. Check client-side cached translation
  try {
    const cacheKey = `blog_trans_${currentLang}_${encodeURIComponent(excerpt.trim().slice(0, 100))}_${excerpt.trim().length}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) return cached;
  } catch (e) {}

  return excerpt;
};

// ─── LOCALIZED CONTENT ──────────────────────────────────────────────────────
export const getLocalizedBlogContent = (blog, lang = 'en') => {
  if (!blog) return '';
  const currentLang = normalizeLang(lang);
  const content = typeof blog === 'string' ? blog : (blog.content || '');
  if (currentLang === 'en') return content;

  // 1. Direct language field on blog object
  if (typeof blog === 'object') {
    if (currentLang === 'mr' && (blog.content_mr || blog.contentMr)) {
      return blog.content_mr || blog.contentMr;
    }
    if (currentLang === 'kn' && (blog.content_kn || blog.contentKn)) {
      return blog.content_kn || blog.contentKn;
    }
  }

  const title = (blog.title || '').toLowerCase();

  // 2. Pre-defined dictionary for legacy blogs
  if (title.includes('choosing the right') || (title.includes('food processing') && title.includes('growth'))) {
    if (currentLang === 'mr') {
      return `कोणत्याही अन्न प्रक्रिया व्यवसायाचे यश हे मुख्यत्वे त्याच्या मशिनरीच्या कार्यक्षमतेवर आणि विश्वासार्हतेवर अवलंबून असते. आधुनिक प्रक्रिया उपकरणे केवळ उत्पादन क्षमता वाढवत नाहीत तर उत्पादनाची एकसमान गुणवत्ता, कमीत कमी नासाडी आणि कमी परिचालन खर्च देखील सुनिश्चित करतात. म्हणूनच योग्य मशिनरी निवडणे हा अन्न उत्पादक आणि कृषी प्रक्रिया युनिट्ससाठी सर्वात महत्त्वाचा व्यावसायिक निर्णय आहे.

दिग्विजय इंडस्ट्रीज अचूक-अभियांत्रिकी अन्न प्रक्रिया मशिनरींची विस्तृत श्रेणी ऑफर करते ज्यामध्ये बेदाणा प्रक्रिया मशिन्स, एअर क्लासिफायर्स, कन्व्हेयर सिस्टीम्स, सुका खारीक कटिंग मशिन्स, कडबा कुट्टी मशिन्स, सायझर मशिन्स आणि इंडस्ट्रियल हॉट वॉटर बॉयलर्स यांचा समावेश आहे. प्रत्येक मशीन आधुनिक प्रक्रिया उद्योगांच्या व्यावहारिक गरजा पूर्ण करताना विश्वासार्ह कामगिरी, ऊर्जा कार्यक्षमता आणि दीर्घ सेवा आयुष्य देण्यासाठी डिझाइन केलेले आहे.

उपकरणे निवडण्यापूर्वी व्यवसायांनी उत्पादन क्षमता, उत्पादनाचा प्रकार, ऑटोमेशनची आवश्यकता, देखभाल गरजा आणि भविष्यातील विस्तार क्षमता यासारख्या घटकांचे मूल्यांकन केले पाहिजे. उच्च-गुणवत्तेच्या मशिनरीमध्ये गुंतवणूक केल्याने केवळ उत्पादकता सुधारत नाही तर उत्पादनाची सुसंगतता आणि ग्राहकांचे समाधान देखील वाढते.

कृषी आणि अन्न उद्योगांमध्ये स्वयंचलित प्रक्रिया उपायांची वाढती मागणी पाहता, अनुभवी उत्पादकाकडून विश्वासार्ह मशिनरी निवडणे दीर्घकालीन मूल्य, परिचालन कार्यक्षमता आणि शाश्वत व्यवसाय वाढ प्रदान करते.`;
    }
    if (currentLang === 'kn') {
      return `ಯಾವುದೇ ಆಹಾರ ಸಂಸ್ಕರಣಾ ಉದ್ಯಮದ ಯಶಸ್ಸು ಹೆಚ್ಚಾಗಿ ಅದರ ಯಂತ್ರೋಪಕರಣಗಳ ದಕ್ಷತೆ ಮತ್ತು ವಿಶ್ವಾಸಾರ್ಹತೆಯನ್ನು ಅವಲಂಬಿಸಿರುತ್ತದೆ. ಆಧುನಿಕ ಸಂಸ್ಕರಣಾ ಉಪಕರಣಗಳು ಉತ್ಪಾದನಾ ಸಾಮರ್ಥ್ಯವನ್ನು ಹೆಚ್ಚಿಸುವುದು ಮಾತ್ರವಲ್ಲದೆ ಸ್ಥಿರವಾದ ಉತ್ಪನ್ನ ಗುಣಮಟ್ಟ, ಕನಿಷ್ಠ ತ್ಯಾಜ್ಯ ಮತ್ತು ಕಡಿಮೆ ನಿರ್ವಹಣಾ ವೆಚ್ಚವನ್ನು ಖಚಿತಪಡಿಸುತ್ತವೆ. ಆದ್ದರಿಂದ ಸರಿಯಾದ ಯಂತ್ರೋಪಕರಣಗಳನ್ನು ಆಯ್ಕೆ ಮಾಡುವುದು ಆಹಾರ ತಯಾರಕರು ಮತ್ತು ಕೃಷಿ ಸಂಸ್ಕರಣಾ ಘಟಕಗಳಿಗೆ ಅತ್ಯಂತ ಪ್ರಮುಖವಾದ ವ್ಯಾಪಾರ ನಿರ್ಧಾರವಾಗಿದೆ.

ದಿಗ್ವಿಜಯ್ ಇಂಡಸ್ಟ್ರೀಸ್ ಒಣದ್ರಾಕ್ಷಿ (ಬೆದಾನ) ಸಂಸ್ಕರಣಾ ಯಂತ್ರಗಳು, ಏರ್ ಕ್ಲಾಸಿಫೈಯರ್‌ಗಳು, ಕನ್ವೇಯರ್ ಸಿಸ್ಟಮ್‌ಗಳು, ಒಣ ಖರ್ಜೂರ ಕತ್ತರಿಸುವ ಯಂತ್ರಗಳು, ಮೇವು ಕತ್ತರಿಸುವ ಯಂತ್ರಗಳು, ಸೈಜರ್ ಯಂತ್ರಗಳು ಮತ್ತು ಬಿಸಿನೀರಿನ ಬಾಯ್ಲರ್‌ಗಳು ಸೇರಿದಂತೆ ನಿಖರವಾಗಿ ಎಂಜಿನಿಯರಿಂಗ್ ಮಾಡಿದ ಆಹಾರ ಸಂಸ್ಕರಣಾ ಯಂತ್ರೋಪಕರಣಗಳ ಸಮಗ್ರ ಶ್ರೇಣಿಯನ್ನು ನೀಡುತ್ತದೆ. ಆಧುನಿಕ ಸಂಸ್ಕರಣಾ ಕೈಗಾರಿಕೆಗಳ ಪ್ರಾಯೋಗಿಕ ಅಗತ್ಯಗಳನ್ನು ಪೂರೈಸುವಾಗ ವಿಶ್ವಾಸಾರ್ಹ ಕಾರ್ಯಕ್ಷಮತೆ, ಶಕ್ತಿ ದಕ್ಷತೆ ಮತ್ತು ದೀರ್ಘಾವಧಿಯ ಬಾಳಿಕೆಯನ್ನು ನೀಡಲು ಪ್ರತಿಯೊಂದು ಯಂತ್ರವನ್ನು ವಿನ್ಯಾಸಗೊಳಿಸಲಾಗಿದೆ.

ಉಪಕರಣಗಳನ್ನು ಆಯ್ಕೆ ಮಾಡುವ ಮೊದಲು ಉದ್ಯಮಗಳು ಉತ್ಪಾದನಾ ಸಾಮರ್ಥ್ಯ, ಉತ್ಪನ್ನದ ಪ್ರಕಾರ, ಆಟೋಮೇಷನ್ ಅಗತ್ಯತೆಗಳು, ನಿರ್ವಹಣಾ ಅಗತ್ಯಗಳು ಮತ್ತು ಭವಿಷ್ಯದ ವಿಸ್ತರಣೆಯಂತಹ ಅಂಶಗಳನ್ನು ಮೌಲ್ಯಮಾಪನ ಮಾಡಬೇಕು. ಉತ್ತಮ ಗುಣಮಟ್ಟದ ಯಂತ್ರೋಪಕರಣಗಳಲ್ಲಿ ಹೂಡಿಕೆ ಮಾಡುವುದರಿಂದ ಉತ್ಪಾದಕತೆ ಸುಧಾರಿಸುವುದು ಮಾತ್ರವಲ್ಲದೆ ಉತ್ಪನ್ನದ ಗುಣಮಟ್ಟ ಮತ್ತು ಗ್ರಾಹಕರ ತೃಪ್ತಿಯನ್ನು ಹೆಚ್ಚಿಸುತ್ತದೆ.

ಕೃಷಿ ಮತ್ತು ಆಹಾರ ಕೈಗಾರಿಕೆಗಳಲ್ಲಿ ಸ್ವಯಂಚಾಲಿತ ಸಂಸ್ಕರಣಾ ಪರಿಹಾರಗಳಿಗೆ ಹೆಚ್ಚುತ್ತಿರುವ ಬೇಡಿಕೆಯೊಂದಿಗೆ, ಅನುಭವಿ ತಯಾರಕರಿಂದ ವಿಶ್ವಾಸಾರ್ಹ ಯಂತ್ರೋಪಕರಣಗಳನ್ನು ಆಯ್ಕೆ ಮಾಡುವುದು ದೀರ್ಘಾವಧಿಯ ಮೌಲ್ಯ, ಕಾರ್ಯಾಚರಣೆಯ ದಕ್ಷತೆ ಮತ್ತು ಸುಸ್ಥಿರ ವ್ಯಾಪಾರ ಬೆಳವಣಿಗೆಯನ್ನು ಒದಗಿಸುತ್ತದೆ.`;
    }
    return content;
  }

  if (title.includes('conveyor') && (title.includes('productivity') || title.includes('material handling'))) {
    if (currentLang === 'mr') {
      return `आधुनिक औद्योगिक कामकाजात कार्यक्षम मटेरिअल हँडलिंग अत्यंत महत्त्वाची भूमिका बजावते. उत्पादनांची मॅन्युअल वाहतूक केवळ मौल्यवान वेळच घेत नाही तर मजुरीचा खर्च आणि उत्पादनाचे नुकसान होण्याचा धोकाही वाढवते. कन्व्हेयर बेल्ट सिस्टीम संपूर्ण उत्पादन प्रक्रियेदरम्यान साहित्याची अखंड हालचाल सक्षम करून एक विश्वासार्ह आणि कार्यक्षम उपाय प्रदान करतात.

दिग्विजय इंडस्ट्रीज कृषी प्रक्रिया, अन्न उत्पादन, गोदामे, लॉजिस्टिक्स सुविधा आणि औद्योगिक उत्पादन लाईन्ससाठी डिझाइन केलेल्या टिकाऊ कन्व्हेयर बेल्ट सोल्यूशन्सचे उत्पादन करते. अखंड कामकाजासाठी तयार केलेल्या या प्रणाली सुरळीत आणि सुरक्षित उत्पादन वाहतूक सुनिश्चित करताना कार्यप्रवाहाची कार्यक्षमता सुधारतात.

आधुनिक कन्व्हेयर सिस्टीम डाउनटाईम कमी करून, मॅन्युअल हाताळणी कमी करून आणि कामाच्या ठिकाणी सुरक्षितता सुधारून व्यवसायांना उत्पादन अनुकूल करण्यास मदत करतात. त्यांचे लवचिक कॉन्फिगरेशन विद्यमान उत्पादन लाईन्समध्ये अखंड एकत्रीकरणास अनुमती देते, ज्यामुळे ते विविध आकारांच्या व्यवसायांसाठी योग्य ठरतात.

उद्योग ऑटोमेशनकडे वाटचाल करत असताना, विश्वासार्ह कन्व्हेयर सोल्यूशन्समध्ये गुंतवणूक केल्याने संस्थांना परिचालन कार्यक्षमता सुधारण्यास, उत्पादकता वाढवण्यास आणि एकूण परिचालन खर्च कमी करताना उत्पादनाचा सातत्यपूर्ण प्रवाह राखण्यास मदत होते.`;
    }
    if (currentLang === 'kn') {
      return `ಆಧುನಿಕ ಕೈಗಾರಿಕಾ ಕಾರ್ಯಾಚರಣೆಗಳಲ್ಲಿ ಸಮರ್ಥ ಮೆಟೀರಿಯಲ್ ಹ್ಯಾಂಡ್ಲಿಂಗ್ ಪ್ರಮುಖ ಪಾತ್ರ ವಹಿಸುತ್ತದೆ. ಉತ್ಪನ್ನಗಳನ್ನು ಮಾನವ ಶ್ರಮದಿಂದ ಸಾಗಿಸುವುದು ಅಮೂಲ್ಯವಾದ ಸಮಯವನ್ನು ತೆಗೆದುಕೊಳ್ಳುವುದು ಮಾತ್ರವಲ್ಲದೆ ಕಾರ್ಮಿಕ ವೆಚ್ಚವನ್ನು ಮತ್ತು ಉತ್ಪನ್ನ ಹಾನಿಯ ಅಪಾಯವನ್ನು ಹೆಚ್ಚಿಸುತ್ತದೆ. ಕನ್ವೇಯರ್ ಬೆಲ್ಟ್ ಸಿಸ್ಟಮ್‌ಗಳು ಉತ್ಪಾದನಾ ಪ್ರಕ್ರಿಯೆಯುದ್ದಕ್ಕೂ ವಸ್ತುಗಳ ನಿರಂತರ ಚಲನೆಯನ್ನು ಸಕ್ರಿಯಗೊಳಿಸುವ ಮೂಲಕ ವಿಶ್ವಾಸಾರ್ಹ ಮತ್ತು ಪರಿಣಾಮಕಾರಿ ಪರಿಹಾರವನ್ನು ನೀಡುತ್ತವೆ.

ದಿಗ್ವಿಜಯ್ ಇಂಡಸ್ಟ್ರೀಸ್ ಕೃಷಿ ಸಂಸ್ಕರಣೆ, ಆಹಾರ ತಯಾರಿಕೆ, ಗೋದಾಮುಗಳು, ಲಾಜಿಸ್ಟಿಕ್ಸ್ ಸೌಲಭ್ಯಗಳು ಮತ್ತು ಕೈಗಾರಿಕಾ ಉತ್ಪಾದನಾ ಮಾರ್ಗಗಳಿಗಾಗಿ ವಿನ್ಯಾಸಗೊಳಿಸಲಾದ ಬಾಳಿಕೆ ಬರುವ ಕನ್ವೇಯರ್ ಬೆಲ್ಟ್ ಪರಿಹಾರಗಳನ್ನು ತಯಾರಿಸುತ್ತದೆ. ನಿರಂತರ ಕಾರ್ಯಾಚರಣೆಗಾಗಿ ನಿರ್ಮಿಸಲಾದ ಈ ವ್ಯವಸ್ಥೆಗಳು ಸುಗಮ ಮತ್ತು ಸುರಕ್ಷಿತ ಉತ್ಪನ್ನ ಸಾಗಣೆಯನ್ನು ಖಚಿತಪಡಿಸಿಕೊಳ್ಳುವ ಮೂಲಕ ಕೆಲಸದ ಹರಿವಿನ ದಕ್ಷತೆಯನ್ನು ಸುಧಾರಿಸುತ್ತವೆ.

ಆಧುನಿಕ ಕನ್ವೇಯರ್ ವ್ಯವಸ್ಥೆಗಳು ಕೆಲಸ ನಿಲ್ಲುವ ಸಮಯವನ್ನು ಕಡಿಮೆ ಮಾಡುವ ಮೂಲಕ, ಹಸ್ತಚಾಲಿತ ನಿರ್ವಹಣೆಯನ್ನು ಕಡಿಮೆ ಮಾಡುವ ಮೂಲಕ ಮತ್ತು ಕೆಲಸದ ಸ್ಥಳದ ಸುರಕ್ಷತೆಯನ್ನು ಸುಧಾರಿಸುವ ಮೂಲಕ ಉತ್ಪಾದನೆಯನ್ನು ಗರಿಷ್ಠಗೊಳಿಸಲು ಉದ್ಯಮಗಳಿಗೆ ಸಹಾಯ ಮಾಡುತ್ತವೆ. ಅವುಗಳ ಹೊಂದಿಕೊಳ್ಳುವ ವಿನ್ಯಾಸವು ಅಸ್ತಿತ್ವದಲ್ಲಿರುವ ಉತ್ಪಾದನಾ ಮಾರ್ಗಗಳಲ್ಲಿ ಸುಲಭವಾಗಿ ಹೊಂದಿಕೊಳ್ಳಲು ಅನುವು ಮಾಡಿಕೊಡುತ್ತದೆ.

ಕೈಗಾರಿಕೆಗಳು ಆಟೋಮೇಷನ್ ಕಡೆಗೆ ಸಾಗುತ್ತಿರುವಂತೆ, ವಿಶ್ವಾಸಾರ್ಹ ಕನ್ವೇಯರ್ ಪರಿಹಾರಗಳಲ್ಲಿ ಹೂಡಿಕೆ ಮಾಡುವುದು ಸಂಸ್ಥೆಗಳಿಗೆ ಕಾರ್ಯಾಚರಣೆಯ ದಕ್ಷತೆಯನ್ನು ಸುಧಾರಿಸಲು, ಉತ್ಪಾದಕತೆಯನ್ನು ಹೆಚ್ಚಿಸಲು ಮತ್ತು ಒಟ್ಟಾರೆ ವೆಚ್ಚವನ್ನು ಕಡಿಮೆ ಮಾಡಲು ಸಹಾಯ ಮಾಡುತ್ತದೆ.`;
    }
    return content;
  }

  if (title.includes('bedana') || (title.includes('raisin') && title.includes('essential'))) {
    if (currentLang === 'mr') {
      return `कार्यक्षमता आणि उत्पादनाची गुणवत्ता सुधारण्यासाठी व्यवसाय आधुनिक ऑटोमेशनचा अवलंब करत असल्याने गेल्या दशकात बेदाणा प्रक्रिया उद्योगात लक्षणीय बदल झाले आहेत. पारंपारिक मॅन्युअल प्रक्रिया पद्धतींमुळे अनेकदा विसंगत उत्पादन, जास्त मजुरी खर्च आणि उत्पादनाचे नुकसान होते. आधुनिक बेदाणा प्रक्रिया मशिन्स बेदाण्याची नैसर्गिक गुणवत्ता टिकवून जलद, स्वच्छ आणि अधिक अचूक प्रक्रिया प्रदान करून या आव्हानांवर मात करतात.

दिग्विजय इंडस्ट्रीज उच्च-कार्यक्षमतेच्या बेदाणा प्रक्रिया मशिन्सची रचना करते जी अखंड औद्योगिक कामकाजासाठी तयार केलेली आहेत. प्रिमियम-गुणवत्तेचे साहित्य आणि अचूक उत्पादन तंत्रज्ञानाचा वापर करून इंजिनिअर केलेली ही मशिन्स सुरळीत कार्यप्रवाह, उच्च उत्पादन क्षमता आणि किमान देखभाल आवश्यकता देतात. त्यांचे मजबूत बांधकाम कठीण प्रक्रिया परिस्थितीतही दीर्घ सेवा आयुष्य सुनिश्चित करते.

लहान बॅचेस असोत किंवा मोठे व्यावसायिक व्हॉल्यूम असोत, आधुनिक बेदाणा मशिन्स मानवी हस्तक्षेप कमी करून आणि उत्पादनाची सुसंगत गुणवत्ता राखून परिचालन कार्यक्षमता सुधारण्यास मदत करतात. वाढलेली उत्पादकता, कमी परिचालन खर्च आणि सुधारित प्रक्रियेचा व्यवसायांना मोठा फायदा होतो.

कृषी आणि अन्न प्रक्रिया उद्योग ऑटोमेशनचा स्वीकार करत असल्याने, शाश्वत वाढ, चांगल्या उत्पादनाची गुणवत्ता आणि दीर्घकालीन व्यावसायिक यशासाठी प्रगत बेदाणा प्रक्रिया मशिनरीमध्ये गुंतवणूक करणे हा एक अत्यंत फायदेशीर आणि व्यावहारिक निर्णय ठरला आहे.`;
    }
    if (currentLang === 'kn') {
      return `ದಕ್ಷತೆ ಮತ್ತು ಉತ್ಪನ್ನದ ಗುಣಮಟ್ಟವನ್ನು ಸುಧಾರಿಸಲು ಉದ್ಯಮಗಳು ಆಧುನಿಕ ಆಟೋಮೇಷನ್ ಅನ್ನು ಅಳವಡಿಸಿಕೊಳ್ಳುತ್ತಿರುವುದರಿಂದ ಕಳೆದ ದಶಕದಲ್ಲಿ ಒಣದ್ರಾಕ್ಷಿ ಸಂಸ್ಕರಣಾ ಉದ್ಯಮವು ಗಣನೀಯವಾಗಿ ಬದಲಾಗಿದೆ. ಸಾಂಪ್ರದಾಯಿಕ ಹಸ್ತಚಾಲಿತ ಸಂಸ್ಕರಣಾ ವಿಧಾನಗಳು ಸಾಮಾನ್ಯವಾಗಿ ಅಸಮಂಜಸ ಉತ್ಪಾದನೆ, ಹೆಚ್ಚಿನ ಕಾರ್ಮಿಕ ವೆಚ್ಚ ಮತ್ತು ಉತ್ಪನ್ನ ನಷ್ಟಕ್ಕೆ ಕಾರಣವಾಗುತ್ತವೆ. ಆಧುನಿಕ ಬೆದಾನ ಸಂಸ್ಕರಣಾ ಯಂತ್ರಗಳು ಒಣದ್ರಾಕ್ಷಿಗಳ ನೈಸರ್ಗಿಕ ಗುಣಮಟ್ಟವನ್ನು ಕಾಪಾಡಿಕೊಳ್ಳುವ ಮೂಲಕ ವೇಗವಾದ, ಸ್ವಚ್ಛವಾದ ಮತ್ತು ಹೆಚ್ಚು ನಿಖರವಾದ ಸಂಸ್ಕರಣೆಯನ್ನು ಒದಗಿಸುತ್ತವೆ.

ದಿಗ್ವಿಜಯ್ ಇಂಡಸ್ಟ್ರೀಸ್ ನಿರಂತರ ಕೈಗಾರಿಕಾ ಕಾರ್ಯಾಚರಣೆಗಾಗಿ ನಿರ್ಮಿಸಲಾದ ಉನ್ನತ-ಕಾರ್ಯಕ್ಷಮತೆಯ ಬೆದಾನ ಸಂಸ್ಕರಣಾ ಯಂತ್ರಗಳನ್ನು ವಿನ್ಯಾಸಗೊಳಿಸುತ್ತದೆ. ಪ್ರೀಮಿಯಂ ಗುಣಮಟ್ಟದ ವಸ್ತುಗಳು ಮತ್ತು ನಿಖರ ಉತ್ಪಾದನಾ ತಂತ್ರಜ್ಞಾನವನ್ನು ಬಳಸಿ ನಿರ್ಮಿಸಲಾದ ಈ ಯಂತ್ರಗಳು ಸುಗಮ ಕೆಲಸದ ಹರಿವು, ಹೆಚ್ಚಿನ ಉತ್ಪಾದನಾ ಸಾಮರ್ಥ್ಯ ಮತ್ತು ಕನಿಷ್ಠ ನಿರ್ವಹಣೆಯನ್ನು ನೀಡುತ್ತವೆ. ಅವುಗಳ ಬಲವಾದ ವಿನ್ಯಾಸವು ಕಠಿಣ ಪರಿಸ್ಥಿತಿಗಳಲ್ಲಿಯೂ ದೀರ್ಘ ಸೇವಾ ಜೀವನವನ್ನು ಖಚಿತಪಡಿಸುತ್ತದೆ.

ಸಣ್ಣ ಪ್ರಮಾಣದ ಉತ್ಪಾದನೆಯಾಗಿರಲಿ ಅಥವಾ ದೊಡ್ಡ ವಾಣಿಜ್ಯ ಪ್ರಮಾಣವಾಗಿರಲಿ, ಆಧುನಿಕ ಬೆದಾನ ಯಂತ್ರಗಳು ಹಸ್ತಚಾಲಿತ ಮಧ್ಯಸ್ಥಿಕೆಯನ್ನು ಕಡಿಮೆ ಮಾಡುವ ಮೂಲಕ ಮತ್ತು ಸ್ಥಿರ ಉತ್ಪನ್ನ ಗುಣಮಟ್ಟವನ್ನು ಕಾಪಾಡುವ ಮೂಲಕ ಕಾರ್ಯಾಚರಣೆಯ ದಕ್ಷತೆಯನ್ನು ಸುಧಾರಿಸಲು ಸಹಾಯ ಮಾಡುತ್ತವೆ. ವ್ಯವಹಾರಗಳು ಹೆಚ್ಚಿದ ಉತ್ಪಾದಕತೆ ಮತ್ತು ಕಡಿಮೆ ನಿರ್ವಹಣಾ ವೆಚ್ಚದ ಪ್ರಯೋಜನವನ್ನು ಪಡೆಯುತ್ತವೆ.

ಕೃಷಿ ಮತ್ತು ಆಹಾರ ಸಂಸ್ಕರಣಾ ಕೈಗಾರಿಕೆಗಳು ಆಟೋಮೇಷನ್ ಅನ್ನು ಮುಂದುವರಿಸುತ್ತಿರುವಂತೆ, ಸುಸ್ಥಿರ ಬೆಳವಣಿಗೆ, ಉತ್ತಮ ಉತ್ಪನ್ನ ಗುಣಮಟ್ಟ ಮತ್ತು ದೀರ್ಘಾವಧಿಯ ಯಶಸ್ಸನ್ನು ಬಯಸುವ ವ್ಯವಹಾರಗಳಿಗೆ ಸುಧಾರಿತ ಬೆದಾನ ಸಂಸ್ಕರಣಾ ಯಂತ್ರೋಪಕರಣಗಳಲ್ಲಿ ಹೂಡಿಕೆ ಮಾಡುವುದು ಅತ್ಯಗತ್ಯ ಪರಿಹಾರವಾಗಿದೆ.`;
    }
    return content;
  }

  // 3. Check client-side cached translation
  try {
    const cacheKey = `blog_trans_${currentLang}_${encodeURIComponent(content.trim().slice(0, 100))}_${content.trim().length}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) return cached;
  } catch (e) {}

  return content;
};

// ─── LOCALIZED AUTHOR ───────────────────────────────────────────────────────
export const getLocalizedBlogAuthor = (author, lang = 'en', blog = null) => {
  if (!author) return '';
  const currentLang = normalizeLang(lang);

  // 1. Direct language field on blog object
  if (blog && typeof blog === 'object') {
    if (currentLang === 'mr' && (blog.author_mr || blog.authorMr)) {
      return blog.author_mr || blog.authorMr;
    }
    if (currentLang === 'kn' && (blog.author_kn || blog.authorKn)) {
      return blog.author_kn || blog.authorKn;
    }
  }

  const lower = author.toLowerCase();
  if (lower.includes('digvijay') || lower.includes('team')) {
    if (currentLang === 'mr') return 'दिग्विजय इंडस्ट्रीज टीम';
    if (currentLang === 'kn') return 'ದಿಗ್ವಿಜಯ್ ಇಂಡಸ್ಟ್ರೀಸ್ ತಂಡ';
    return author;
  }

  // Check client-side cached translation
  try {
    const cacheKey = `blog_trans_${currentLang}_${encodeURIComponent(author.trim().slice(0, 100))}_${author.trim().length}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) return cached;
  } catch (e) {}

  return author;
};

// ─── LOCALIZED TAGS ─────────────────────────────────────────────────────────
export const getLocalizedBlogTag = (tag, lang = 'en') => {
  if (!tag) return '';
  const currentLang = normalizeLang(lang);
  const lower = tag.trim().toLowerCase();

  if (lower === 'food processing machinery') {
    return currentLang === 'mr' ? 'अन्न प्रक्रिया मशिनरी' : currentLang === 'kn' ? 'ಆಹಾರ ಸಂಸ್ಕರಣಾ ಯಂತ್ರೋಪಕರಣ' : tag;
  }
  if (lower === 'agricultural equipment') {
    return currentLang === 'mr' ? 'कृषी उपकरणे' : currentLang === 'kn' ? 'ಕೃಷಿ ಉಪಕರಣಗಳು' : tag;
  }
  if (lower === 'industrial machines') {
    return currentLang === 'mr' ? 'औद्योगिक मशिन्स' : currentLang === 'kn' ? 'ಕೈಗಾರಿಕಾ ಯಂತ್ರಗಳು' : tag;
  }
  if (lower === 'processing plant') {
    return currentLang === 'mr' ? 'प्रक्रिया प्रकल्प' : currentLang === 'kn' ? 'ಸಂಸ್ಕರಣಾ ಘಟಕ' : tag;
  }
  if (lower === 'maharashtra') {
    return currentLang === 'mr' ? 'महाराष्ट्र' : currentLang === 'kn' ? 'ಮಹಾರಾಷ್ಟ್ರ' : tag;
  }
  if (lower === 'sangli') {
    return currentLang === 'mr' ? 'सांगली' : currentLang === 'kn' ? 'ಸಾಂಗ್ಲಿ' : tag;
  }
  if (lower === 'automation solutions' || lower === 'automation') {
    return currentLang === 'mr' ? 'ऑटोमेशन सोल्यूशन्स' : currentLang === 'kn' ? 'ಆಟೋಮೇಷನ್ ಪರಿಹಾರಗಳು' : tag;
  }
  if (lower === 'digvijay industries') {
    return currentLang === 'mr' ? 'दिग्विजय इंडस्ट्रीज' : currentLang === 'kn' ? 'ದಿಗ್ವಿಜಯ್ ಇಂಡಸ್ಟ್ರೀಸ್' : tag;
  }
  if (lower === 'conveyor belt') {
    return currentLang === 'mr' ? 'कन्व्हेयर बेल्ट' : currentLang === 'kn' ? 'ಕನ್ವೇಯರ್ ಬೆಲ್ಟ್' : tag;
  }
  if (lower === 'material handling') {
    return currentLang === 'mr' ? 'मटेरिअल हँडलिंग' : currentLang === 'kn' ? 'ಮೆಟೀರಿಯಲ್ ಹ್ಯಾಂಡ್ಲಿಂಗ್' : tag;
  }
  if (lower === 'industrial conveyor') {
    return currentLang === 'mr' ? 'औद्योगिक कन्व्हेयर' : currentLang === 'kn' ? 'ಕೈಗಾರಿಕಾ ಕನ್ವೇಯರ್' : tag;
  }
  if (lower === 'manufacturing equipment') {
    return currentLang === 'mr' ? 'उत्पादन उपकरणे' : currentLang === 'kn' ? 'ಉತ್ಪಾದನಾ ಉಪಕರಣಗಳು' : tag;
  }
  if (lower === 'warehouse solutions') {
    return currentLang === 'mr' ? 'वेअरहाऊस सोल्यूशन्स' : currentLang === 'kn' ? 'ಗೋದಾಮು ಪರಿಹಾರಗಳು' : tag;
  }
  if (lower === 'bedana processing machine') {
    return currentLang === 'mr' ? 'बेदाणा प्रक्रिया मशीन' : currentLang === 'kn' ? 'ಬೆದಾನ ಸಂಸ್ಕರಣಾ ಯಂತ್ರ' : tag;
  }
  if (lower === 'raisin processing machine') {
    return currentLang === 'mr' ? 'मनुका प्रक्रिया मशीन' : currentLang === 'kn' ? 'ಒಣದ್ರಾಕ್ಷಿ ಸಂಸ್ಕರಣಾ ಯಂತ್ರ' : tag;
  }
  if (lower === 'agricultural machinery') {
    return currentLang === 'mr' ? 'कृषी यंत्रसामग्री' : currentLang === 'kn' ? 'ಕೃಷಿ ಯಂತ್ರೋಪಕರಣಗಳು' : tag;
  }
  if (lower === 'food processing') {
    return currentLang === 'mr' ? 'अन्न प्रक्रिया' : currentLang === 'kn' ? 'ಆಹಾರ ಸಂಸ್ಕರಣೆ' : tag;
  }
  if (lower === 'industrial automation') {
    return currentLang === 'mr' ? 'औद्योगिक ऑटोमेशन' : currentLang === 'kn' ? 'ಕೈಗಾರಿಕಾ ಆಟೋಮೇಷನ್' : tag;
  }

  // Check client-side cached translation
  try {
    const cacheKey = `blog_trans_${currentLang}_${encodeURIComponent(tag.trim().slice(0, 100))}_${tag.trim().length}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) return cached;
  } catch (e) {}

  return tag;
};

export const getLocalizedBlogTags = (tags, lang = 'en', blog = null) => {
  const currentLang = normalizeLang(lang);
  if (blog && typeof blog === 'object') {
    if (currentLang === 'mr' && blog.tags_mr) {
      if (Array.isArray(blog.tags_mr) && blog.tags_mr.length > 0) return blog.tags_mr;
      if (typeof blog.tags_mr === 'string' && blog.tags_mr.trim()) {
        return blog.tags_mr.split(',').map(s => s.trim()).filter(Boolean);
      }
    }
    if (currentLang === 'kn' && blog.tags_kn) {
      if (Array.isArray(blog.tags_kn) && blog.tags_kn.length > 0) return blog.tags_kn;
      if (typeof blog.tags_kn === 'string' && blog.tags_kn.trim()) {
        return blog.tags_kn.split(',').map(s => s.trim()).filter(Boolean);
      }
    }
  }

  if (!Array.isArray(tags)) return [];
  return tags.map(t => getLocalizedBlogTag(t, lang));
};
